import { 
  Zap, 
  Settings, 
  FileText, 
  Wrench, 
  Users, 
  Home, 
  Network, 
  Lightbulb 
} from "lucide-react";
import { AnimatedSection } from "./AnimatedSection";

const services = [
  {
    icon: Zap,
    title: "INGENIERÍA ELÉCTRICA",
    description: "Diseño de sistemas eléctricos en media y baja tensión. Cálculos de demanda máxima, coordinación de protecciones, diagramas unifilares y memorias técnicas según normativa SEC NCh Elec 4/2003."
  },
  {
    icon: Settings,
    title: "DISEÑO & MONTAJES",
    description: "Fabricación de tableros de distribución, fuerza y control. Montaje de canalizaciones EMT, IMC, bandeja portacables y sistemas de puesta a tierra con malla equipotencial."
  },
  {
    icon: FileText,
    title: "PROYECTOS ELÉCTRICOS",
    description: "Gestión integral bajo metodología PMI. Ingeniería de detalle, cubicación, coordinación de especialidades, supervisión de obra y tramitación TE1/TE4 ante SEC."
  },
  {
    icon: Wrench,
    title: "MANTENCIÓN ELÉCTRICA",
    description: "Mantención preventiva y correctiva con análisis termográfico, medición de aislación con megger, pruebas de continuidad y protocolos de bloqueo/etiquetado LOTO."
  },
  {
    icon: Users,
    title: "CONSULTORÍA",
    description: "Auditorías energéticas clase I y II, estudios de calidad de energía, análisis de armónicos, factor de potencia y dimensionamiento de bancos de condensadores."
  },
  {
    icon: Home,
    title: "INSTALACIONES",
    description: "Instalaciones eléctricas industriales y comerciales. Alimentadores, circuitos derivados, sistemas de respaldo UPS/grupos electrógenos y transferencias automáticas."
  },
  {
    icon: Network,
    title: "CABLEADO ESTRUCTURADO",
    description: "Redes Cat5e, Cat6, Cat6A y Cat7. Fibra óptica OM1-OM4 (multimodo) y OS1/OS2 (monomodo). Certificación Fluke con reportes OTDR y pruebas de canal permanente."
  },
  {
    icon: Lightbulb,
    title: "ILUMINACIÓN",
    description: "Diseño luminotécnico con DIALux. Instalación de luminarias LED industriales, comerciales y de emergencia. Cálculo de luxes, uniformidad y eficiencia lm/W según normativa."
  }
];

interface ServiceCardProps {
  service: typeof services[0];
  index: number;
}

const ServiceCard = ({ service, index }: ServiceCardProps) => {
  const Icon = service.icon;
  
  return (
    <AnimatedSection animation="fade-in" delay={index * 100}>
      <div className="group h-full glass-liquid-medium bg-white/5 backdrop-blur-xl rounded-2xl border border-white/15 hover:border-primary/50 transition-all duration-500 hover:shadow-liquid-xl p-6 lg:p-8 relative overflow-hidden">
        {/* Shimmer Effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 group-hover:translate-x-full transition-all duration-1000 ease-out pointer-events-none" />
        
        {/* Glow Effect */}
        <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500 pointer-events-none" />
        
        <div className="relative z-10">
          {/* Icon Container */}
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center shadow-liquid-lg group-hover:shadow-liquid-xl group-hover:scale-110 transition-all duration-500">
              <Icon className="w-10 h-10 text-primary-foreground" strokeWidth={1.5} />
            </div>
          </div>
          
          {/* Title */}
          <h3 className="text-xl lg:text-2xl font-bold text-white text-center mb-3 font-montserrat tracking-wide group-hover:text-primary transition-colors duration-300">
            {service.title}
          </h3>
          
          {/* Decorative Divider */}
          <div className="flex justify-center mb-4">
            <div className="w-12 h-1 bg-gradient-to-r from-transparent via-primary to-transparent rounded-full group-hover:w-20 transition-all duration-500" />
          </div>
          
          {/* Description */}
          <p className="text-sm lg:text-base text-white/80 text-justify leading-relaxed hyphens-auto" style={{ textAlignLast: 'center', wordSpacing: '-0.5px' }}>
            {service.description}
          </p>
        </div>
      </div>
    </AnimatedSection>
  );
};

const Services = () => {
  return (
    <section 
      id="servicios" 
      className="relative py-24 lg:py-32 overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-dark-section via-industrial-gray/95 to-dark-section" />
      
      {/* Animated Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-liquid-blob" />
        <div className="absolute bottom-1/4 -right-32 w-80 h-80 bg-accent/10 rounded-full blur-3xl animate-liquid-blob" style={{ animationDelay: '2s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />
      </div>
      
      {/* Subtle Pattern Overlay */}
      <div 
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }}
      />
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <AnimatedSection animation="fade-in" className="text-center mb-16">
          <span className="inline-block px-4 py-2 glass-liquid-light rounded-full text-primary text-sm font-semibold tracking-wider mb-6 border border-primary/30">
            SOLUCIONES INTEGRALES
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 font-montserrat">
            NUESTROS SERVICIOS
          </h2>
          <p className="text-base lg:text-lg text-white/70 max-w-2xl mx-auto leading-relaxed">
            Soluciones eléctricas completas con más de 16 años de experiencia, 
            garantizando calidad, seguridad y cumplimiento normativo SEC.
          </p>
        </AnimatedSection>
        
        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {services.map((service, index) => (
            <ServiceCard key={index} service={service} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
